/**
 * Cartes en attente du planning (docs/ux-v2/SPEC.md § 12) : premier chargement, changement de semaine ou de marque.
 *
 * Même dessin et même feuille que le squelette rendu par le serveur avant le JS (apps/server/UxV2Skeleton.php et
 * UxV2Skeleton.css, importée ici) : React le remplace à son premier rendu sans que rien ne bouge ni ne change d'aspect.
 * Le balisage d'une carte est celui de UxV2Skeleton::card (contrôle : lib/planning/skeleton.test.ts). Reflet calé sur
 * celui du serveur (lib/planning/skeleton.ts alignSkeletonAnimations) ; rien de focalisable, tout en aria-hidden.
 */
import { useLayoutEffect, useRef, type CSSProperties } from 'react';
import { alignSkeletonAnimations, isTallSkeletonCard } from '../../lib/planning/skeleton';
import { injectV2Css } from '../../styles/inject';
// Feuille partagée avec le serveur (imprimée en ligne par UxV2Skeleton.php) : une seule source pour les deux rendus.
import skeletonCss from '../../../../../server/UxV2Skeleton.css?inline';

injectV2Css('server/UxV2Skeleton.css', skeletonCss);

/** Cartes en attente par jour (= UxV2Skeleton::GRID_CARDS, ROW_CARDS). */
export const GRID_SKELETON_CARDS = 4;
export const ROW_SKELETON_CARDS = 5;

/** Décalage du reflet d'une colonne à la suivante (= UxV2Skeleton.css, .mmv2-sk__day:nth-child). */
export const SKELETON_COLUMN_DELAY = 70;

interface SkeletonCardProps {
  /** Vue multi-marques : bloc de l'étiquette de marque, en haut à droite. */
  brandTag: boolean;
  tall?: boolean;
  /** Carte en ligne (vue jour). */
  row?: boolean;
}

export function SkeletonCard({ brandTag, tall = false, row = false }: SkeletonCardProps) {
  const className = ['mmv2-sk-card', 'mmv2-sk-shine', row ? 'mmv2-sk-card--row' : '', tall ? 'mmv2-sk-card--tall' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={className}>
      <span className="mmv2-sk-card__top">
        <span className="mmv2-sk-b mmv2-sk-card__time" />
        {brandTag ? <span className="mmv2-sk-b mmv2-sk-card__brand" /> : null}
      </span>
      <span className="mmv2-sk-b mmv2-sk-card__name" />
      {tall ? <span className="mmv2-sk-b mmv2-sk-card__theme" /> : null}
      <span className="mmv2-sk-b mmv2-sk-card__meta" />
      <span className="mmv2-sk-b mmv2-sk-card__tag" />
    </div>
  );
}

interface SkeletonListProps {
  /** Classe de la liste réelle qu'elle annonce (.mmv2-day__list, .mmv2-dayview__list). */
  className: string;
  /** Colonne de la grille (0 = lundi), décalage du reflet ; vue jour : null. */
  day: number | null;
  brandTag: boolean;
}

/** Liste de cartes en attente d'une colonne (grille 7 jours) ou du jour choisi (vue jour). */
export function SkeletonList({ className, day, brandTag }: SkeletonListProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Avant la peinture : le reflet reprend à la phase du squelette du serveur (ou de la page), sans à-coup.
  useLayoutEffect(() => alignSkeletonAnimations(ref.current), []);
  const row = day === null;
  const count = row ? ROW_SKELETON_CARDS : GRID_SKELETON_CARDS;
  const style = row ? undefined : ({ '--mmv2-sk-delay': `${day * SKELETON_COLUMN_DELAY}ms` } as CSSProperties);
  return (
    <div ref={ref} className={`${className} mmv2-sk-list`} style={style} aria-hidden="true">
      {Array.from({ length: count }, (_, card) => (
        <SkeletonCard key={card} brandTag={brandTag} row={row} tall={!row && isTallSkeletonCard(day, card)} />
      ))}
    </div>
  );
}
